import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EventCard = ({ event, onSetReminder, onViewDetails }) => {
  const getEventTypeColor = (type) => {
    switch (type) {
      case 'regulatory': return 'bg-error/10 text-error border-error/20';
      case 'earnings': return 'bg-accent/10 text-accent border-accent/20';
      case 'policy': return 'bg-warning/10 text-warning border-warning/20';
      case 'market': return 'bg-success/10 text-success border-success/20';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'regulatory': return 'Shield';
      case 'earnings': return 'DollarSign';
      case 'policy': return 'FileText';
      case 'market': return 'TrendingUp';
      default: return 'Calendar';
    }
  };

  const formatEventDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatEventTime = (date) => {
    return new Date(date)?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCountdownText = (date) => {
    const now = new Date();
    const eventDate = new Date(date);
    const diffInHours = Math.floor((eventDate - now) / (1000 * 60 * 60));
    
    if (diffInHours < 0) return 'Past Event';
    if (diffInHours < 24) return `${diffInHours}h remaining`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d remaining`;
    return `${Math.floor(diffInHours / 168)}w remaining`;
  };

  const isUpcoming = () => {
    const now = new Date();
    const eventDate = new Date(event.date);
    return eventDate > now;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-elevated transition-shadow duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg border ${getEventTypeColor(event?.type)}`}>
            <Icon name={getEventTypeIcon(event?.type)} size={16} />
          </div>
          <div>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {event?.type?.replace('_', ' ')}
            </span>
            <h3 className="text-lg font-semibold text-foreground mt-1">
              {event?.title}
            </h3>
          </div>
        </div>
        {isUpcoming() && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onSetReminder(event?.id)}
            className="p-1"
          >
            <Icon 
              name={event?.hasReminder ? "Bell" : "BellPlus"} 
              size={16}
              className={event?.hasReminder ? "text-accent" : "text-muted-foreground"}
            />
          </Button>
        )}
      </div>
      <div className="mb-4">
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
          {event?.description}
        </p>
      </div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Icon name="Calendar" size={14} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              {formatEventDate(event?.date)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={14} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {formatEventTime(event?.date)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {event?.relevantMarkets?.map((market) => (
            <span
              key={market}
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                market === 'EUA' ?'bg-success/10 text-success' :'bg-primary/10 text-primary'
              }`}
            >
              {market}
            </span>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Icon name="Timer" size={14} className="text-muted-foreground" />
            <span className={`text-sm font-medium ${
              isUpcoming() ? 'text-warning' : 'text-muted-foreground'
            }`}>
              {getCountdownText(event?.date)}
            </span>
          </div>
          {event?.priority && (
            <div className="flex items-center gap-2">
              <Icon name="AlertTriangle" size={14} className="text-error" />
              <span className="text-sm font-medium text-error">High Priority</span>
            </div>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewDetails(event)}
          className="flex items-center gap-2"
        >
          <span>View Details</span>
          <Icon name="ExternalLink" size={14} />
        </Button>
      </div>
    </div>
  );
};

export default EventCard;